import { jsx, jsxs } from 'react/jsx-runtime';
import { ResourcePreview } from './ResourcePreview.js';
import { ResourcePreviewContextMenu } from './ResourcePreviewContextMenu/ResourcePreviewContextMenu.js';
import { useResourcePreviewUiConfigContext } from './resourcePreviewUiConfigContext.js';
import { useDroppableAsTargetToResourceMove } from '../../hooks/useDroppableAsTargetToResourceMove.js';
import { isResourceDetailFolder } from '../../utils/resourceDetailGuards.js';
import { motion } from 'framer-motion';
import React__default, { memo } from 'react';
import { createPortal } from 'react-dom';
import { styled } from 'styled-components';
import { shallow as shallow$1 } from '../../../../node_modules/zustand/esm/shallow.js';
import useMobileSelectionStore from '../../../store/mobileSelection.js';
import useDraggable from '../../../hooks/useDraggable.js';
import { useResponsive } from '@fabric-app/ui/hooks/useResponsive';
import { framerAnimationFade } from '@fabric-app/ui/constants/framerAnimations';
import { preventForwardPropsConfig } from '@fabric-app/ui/utils/preventForwardProps';
import { useResourcePermissionsByCurrentUser } from '../../../resource-detail/components/context/getDisabledActionsReasons.js';
import { isInMobile } from '@fabric-app/compat';
import { useDebounce } from '@fabric-app/hooks/useDebounce';
import { ResourceDataContextProvider } from '../../../resource-detail/components/context/ResourceDataContextProvider.js';
import { RESOURCE_GRID_PREVIEW_CONTENT } from '@fabric-app/ui/theme/resourcePreviewConstants';

const ResourcePreviewEnhancedBase = (props) => {
  const { resource, viewMode = "Grid", canMultiSelect = false, selected, disableContextMenu = false, zoomLevel, colorLabels = [], isSelectable, style, resourcePreviewProps, onClick, droppableProps, draggableProps, ResourcePreviewComponent = ResourcePreview } = props;
  const isExternalResource = !!resource._meta?.externalData?.url;
  const { mobileSelectModeIsActive } = useMobileSelectionStore((state) => ({
    mobileSelectModeIsActive: state.mobileSelectModeIsActive
  }), shallow$1);
  const { isMobileView } = useResponsive();
  const isSelected = canMultiSelect && selected && !draggableProps?.isDragging || !!droppableProps?.isDraggingItemsOver;
  return /* @__PURE__ */ jsx(ListItemWrapper, {
    ref: droppableProps?.droppableRef,
    as: motion.div,
    whileTap: "whileTap",
    variants: (isMobileView || isInMobile()) && !mobileSelectModeIsActive ? {
      whileTap: {
        transition: {
          duration: 0.01
        },
        scale: 0.99
      }
    } : {},
    "data-droppable": Boolean(isResourceDetailFolder(resource) && !draggableProps?.isDragging) && !isExternalResource,
    "data-testid": resource.kind + "-fdoc-item",
    "data-draft": resource.isDraft ? "true" : void 0,
    "data-resource-id": resource.id,
    "data-view-mode": viewMode,
    style: {
      ...style,
      ...draggableProps?.styles
    },
    children: /* @__PURE__ */ jsx(ResourcePreviewContextMenu, {
      contextDisabled: draggableProps?.isDragging || disableContextMenu || isExternalResource,
      onItemClickAction: onClick ? () => onClick(resource) : void 0,
      colorLabels,
      children: /* @__PURE__ */ jsx(ResourcePreviewComponent, {
        resource,
        colorLabels,
        ...resourcePreviewProps,
        appearence: {
          ...resourcePreviewProps?.appearence
        },
        zoomLevel,
        isSelectable,
        onClickResource: onClick,
        onMouseDown: draggableProps?.onMouseDown,
        onDragStart: (e) => e.preventDefault(),
        draggableRef: draggableProps?.draggableRef,
        isSelected
      })
    })
  });
};
const ResourcePreviewEnhancedDesktop = (props) => {
  const { resource, viewMode = "List", disableDrag = false, colorLabels = [], resourcePreviewProps, zoomLevel, disableLayoutAnimation = false, ResourcePreviewComponent = ResourcePreview } = props;
  const draggableRef = React__default.useRef(null);
  const droppableRef = React__default.useRef(null);
  const isReadonly = resource.isReadonly;
  const isExternalResource = !!resource._meta?.externalData?.url;
  const { disabledActions } = useResourcePermissionsByCurrentUser(resource);
  const { isDraggingItemsOver } = useDroppableAsTargetToResourceMove(droppableRef, isResourceDetailFolder(resource) ? resource : void 0, {
    enabled: !isReadonly && !disabledActions.createChildResources && !isExternalResource,
    action: "drop-into-folder"
  });
  const isDraft = Boolean(resource?.isDraft);
  const { dragState, isDragging, onMouseDown, draggingStyles, isDragParent, shouldRender } = useDraggable({
    ref: draggableRef.current,
    disabled: viewMode === "List" || disableDrag || isInMobile() || isReadonly || isDraft || resource.isReadonly || !!disabledActions.move || isExternalResource,
    objectId: resource?.id,
    stopPropagation: viewMode !== "Kanban",
    scale: 0.3,
    objectParentId: resource.parent?.id
  });
  const draggingStylesMerged = React__default.useMemo(() => {
    if (!isDragging) {
      return {};
    }
    if (!isDragParent) {
      return {
        position: "absolute",
        top: 0,
        left: 0,
        width: RESOURCE_GRID_PREVIEW_CONTENT.width,
        height: RESOURCE_GRID_PREVIEW_CONTENT.height,
        ...draggingStyles
      };
    }
    if (isDragParent && dragState) {
      return {
        width: RESOURCE_GRID_PREVIEW_CONTENT.width,
        height: RESOURCE_GRID_PREVIEW_CONTENT.height,
        pointerEvents: "none"
      };
    }
    return {};
  }, [
    isDragging,
    draggingStyles,
    dragState,
    isDragParent
  ]);
  const targetPortal = !isDragging || isDragParent ? document.getElementById("drag-and-drop-portal") : document.querySelector('[data-drag-parent="true"]');
  const dragElement = () => {
    if (!isDragging || !targetPortal || !shouldRender) return null;
    const draggedPreview = /* @__PURE__ */ jsx(ResourcePreviewComponent, {
      onClickResource: void 0,
      resource,
      colorLabels,
      ...resourcePreviewProps,
      zoomLevel,
      style: {
        ...resourcePreviewProps?.style,
        ...draggingStylesMerged,
        pointerEvents: "none"
      },
      appearence: {
        ...resourcePreviewProps?.appearence,
        hideMeta: true
      }
    });
    return /* @__PURE__ */ createPortal(isDragParent ? /* @__PURE__ */ jsx(DragParentContainer, {
      "data-drag-parent": isDragParent,
      style: {
        ...dragState && {
          ...draggingStyles,
          ...dragState,
          width: dragState.width,
          height: dragState.height,
          top: dragState.y,
          left: dragState.x,
          pointerEvents: "none"
        }
      },
      children: draggedPreview
    }) : draggedPreview, targetPortal);
  };
  const debouncedZoomLevel = useDebounce(zoomLevel, 50);
  const layoutIdProps = debouncedZoomLevel === zoomLevel && !disableLayoutAnimation ? {
    layoutId: `${resource.draftId || resource.id}--${zoomLevel}--${props.layoutId || "item"}`,
    layout: "position"
  } : {};
  const uiConfig = useResourcePreviewUiConfigContext();
  return /* @__PURE__ */ jsxs(motion.div, {
    ...layoutIdProps,
    ...framerAnimationFade,
    style: {
      width: "100%",
      ...uiConfig?.appearence?.gridPreviewDimensions
    },
    children: [
      /* @__PURE__ */ jsx(ResourcePreviewEnhancedBase, {
        ...props,
        draggableProps: {
          draggableRef,
          styles: isDragging ? {
            opacity: 0.5,
            pointerEvents: "none"
          } : void 0,
          isDragging,
          onMouseDown
        },
        droppableProps: {
          droppableRef,
          isDraggingItemsOver
        }
      }),
      isDragging && targetPortal && shouldRender && dragElement()
    ]
  });
};
const ResourcePreviewEnhancedMobile = (props) => {
  const uiConfig = useResourcePreviewUiConfigContext();
  return /* @__PURE__ */ jsx("div", {
    style: {
      width: "100%",
      ...uiConfig?.appearence?.gridPreviewDimensions
    },
    children: /* @__PURE__ */ jsx(ResourcePreviewEnhancedBase, {
      ...props
    })
  });
};
const ResourcePreviewEnhanced = (props) => {
  const { isMobileView } = useResponsive();
  return /* @__PURE__ */ jsx(ResourceDataContextProvider, {
    resource: props.resource,
    parentColorLabels: props.colorLabels,
    children: isMobileView ? /* @__PURE__ */ jsx(ResourcePreviewEnhancedMobile, {
      ...props
    }) : /* @__PURE__ */ jsx(ResourcePreviewEnhancedDesktop, {
      ...props
    })
  });
};
const ResourcePreviewEnhanced$1 = /* @__PURE__ */ memo(ResourcePreviewEnhanced, shallow$1);
const ListItemWrapper = styled.div.withConfig(preventForwardPropsConfig([
  "isSelected",
  "viewMode",
  "isDragging"
])).withConfig({
  displayName: "ResourcePreviewEnhanced__ListItemWrapper",
  componentId: "sc-330245c0-0"
})([
  `position:relative;min-height:100%;transform:translateZ(0);`
]);
const DragParentContainer = styled.div.withConfig({
  displayName: "ResourcePreviewEnhanced__DragParentContainer",
  componentId: "sc-330245c0-1"
})([
  `position:fixed;transition:box-shadow 200ms cubic-bezier(0.165,0.84,0.44,1),transform 200ms cubic-bezier(0.165,0.84,0.44,1);will-change:transform,box-shadow,top,left,width,height;`
]);

export { ResourcePreviewEnhanced$1 as default };
//# sourceMappingURL=ResourcePreviewEnhanced.js.map
